/**
 * Add Job Modal Component
 * Requirements: 5.3
 */
import { useState, useRef } from 'react';
import { api } from '../services/api';
import { X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { UpgradePromptModal } from './UpgradePromptModal';

interface AddJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedResume: string;
}

export function AddJobModal({ isOpen, onClose, onSuccess, selectedResume }: AddJobModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [formData, setFormData] = useState({
    jobcompany: '',
    jobtitle: '',
    jobdesc: '',
    joblocation: '',
    jobsalary: '',
    joburl: '',
    jobtags: '',
  });

  const [showJobLimitModal, setShowJobLimitModal] = useState(false);
  // Post-submit outcome the user MUST see: the backend can answer "already in
  // your list" (duplicate) or "filled in the card that was still researching"
  // (merged). Both used to close the modal silently, so a manual entry looked
  // like it vanished (incident 2026-07-27). Mirrors AddJobFromURL's notice.
  const [notice, setNotice] = useState<{ kind: 'duplicate' | 'merged'; text: string } | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  // Closing must never leave a stale notice behind for the next open (the form
  // itself is deliberately preserved so an accidental close doesn't lose typing).
  const handleClose = () => {
    setNotice(null);
    onClose();
  };
  useEscapeClose(handleClose, isOpen);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  function dismissNotice() {
    setNotice(null);
    onClose();
    setAnonymous(false);
    setFormData({
      jobcompany: '',
      jobtitle: '',
      jobdesc: '',
      joblocation: '',
      jobsalary: '',
      joburl: '',
      jobtags: '',
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const result = await api.createJobManual({
        ...formData,
        resumeid: selectedResume,
        jobtags: formData.jobtags.split(',').map((t) => t.trim()).filter(Boolean),
      });

      // Always refresh the list first — for both duplicate and merged the
      // existing card is what the user should now be looking at.
      onSuccess();

      if (result?.duplicate) {
        const title = result.matched_title || formData.jobtitle;
        const company = result.matched_company || formData.jobcompany;
        setNotice({
          kind: 'duplicate',
          text: `This job already exists as "${title}" at "${company}" — showing the existing card.`,
        });
        return;
      }
      if (result?.merged) {
        setNotice({
          kind: 'merged',
          text: 'Updated the existing researching card with your details.',
        });
        return;
      }

      onClose();
      setAnonymous(false);
      setFormData({
        jobcompany: '',
        jobtitle: '',
        jobdesc: '',
        joblocation: '',
        jobsalary: '',
        joburl: '',
        jobtags: '',
      });
    } catch (err: any) {
      // Check for 403 with monthly_job_limit to show upgrade modal
      if (err?.response?.status === 403 && err?.response?.data?.monthly_job_limit) {
        setShowJobLimitModal(true);
      } else {
        setError('Failed to create job. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Add Job Manually">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Add Job Manually</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {notice ? (
          <div className="p-4">
            <div
              role="alert"
              data-testid="addjob-notice"
              className={`mb-4 p-3 rounded-lg text-sm flex items-start ${
                notice.kind === 'duplicate'
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-green-50 text-green-800'
              }`}
            >
              {notice.kind === 'duplicate' ? (
                <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <span>{notice.text}</span>
            </div>
            <div className="flex items-center justify-end pt-4 border-t">
              <button
                type="button"
                onClick={dismissNotice}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Got it
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company *
              </label>
              <input
                type="text"
                required
                data-help-target="mainapp.addjob.manualentry.company"
                disabled={anonymous}
                value={anonymous ? '[Anonymous]' : formData.jobcompany}
                onChange={(e) => setFormData({ ...formData, jobcompany: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${anonymous ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                placeholder="Acme Inc"
              />
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAnonymous(checked);
                    if (checked) {
                      setFormData({ ...formData, jobcompany: '[Anonymous]' });
                    } else {
                      setFormData({ ...formData, jobcompany: '' });
                    }
                  }}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs text-gray-500">Anonymous company</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                required
                data-help-target="mainapp.addjob.manualentry.jobtitle"
                value={formData.jobtitle}
                onChange={(e) => setFormData({ ...formData, jobtitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Senior Software Engineer"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description *
            </label>
            <textarea
              required
              rows={6}
              data-help-target="mainapp.addjob.manualentry.jobdescription"
              value={formData.jobdesc}
              onChange={(e) => setFormData({ ...formData, jobdesc: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Paste the full job description here..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                data-help-target="mainapp.addjob.manualentry.location"
                value={formData.joblocation}
                onChange={(e) => setFormData({ ...formData, joblocation: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Remote / San Francisco, CA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salary
              </label>
              <input
                type="text"
                data-help-target="mainapp.addjob.manualentry.salary"
                value={formData.jobsalary}
                onChange={(e) => setFormData({ ...formData, jobsalary: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="$150,000 - $200,000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job URL
            </label>
            <input
              type="url"
              data-help-target="mainapp.addjob.manualentry.joburl"
              value={formData.joburl}
              onChange={(e) => setFormData({ ...formData, joburl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://company.com/jobs/123"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={formData.jobtags}
              onChange={(e) => setFormData({ ...formData, jobtags: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="remote, senior, python"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {loading && <span role="status" aria-label="Adding job"><Loader2 className="w-4 h-4 mr-2 animate-spin" /></span>}
              Add Job
            </button>
          </div>
        </form>
        )}
      </div>
      <UpgradePromptModal
        isOpen={showJobLimitModal}
        onClose={() => setShowJobLimitModal(false)}
        limitType="job"
        onNavigateToSettings={() => {
          setShowJobLimitModal(false);
          onClose();
        }}
      />
    </div>
  );
}
